import React, { useState, useEffect } from 'react';
import { Share2, Copy, Users, Gift, Check, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import { referralApi } from '../services/api';
import logger from '../lib/logger';

interface ReferralStats {
  referral_code: string;
  referral_url?: string;
  successful_referrals: number;
  referral_bonus_videos: number;
  referred_by?: string;
  referral_activities: any[];
}

const ReferralDashboard: React.FC = () => {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadReferralStats();
  }, []);

  const loadReferralStats = async () => {
    try {
      const response = await referralApi.getStats();
      // console.log('Referral stats response:', response.data); // Debug log
      setStats(response.data);
    } catch (error) {
      logger.error('Error loading referral stats:', error);
      toast.error('Failed to load referral information');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  const shareViaEmail = () => {
    if (!stats?.referral_url) return;
    
    const subject = 'Join ClipThis and get an extra video per month!';
    const body = `Hey! I've been using ClipThis to create amazing clips from my videos, and I thought you'd love it too.

Use my referral code "${stats.referral_code}" when you sign up and we'll both get an extra video per month!

Sign up here: ${stats.referral_url}

ClipThis uses AI to automatically find the best moments in your videos and creates perfect clips for social media. It's been a game-changer for my content!`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  const shareViaTwitter = () => {
    if (!stats?.referral_url) return;
    
    const text = `Just discovered @ClipThis_AI - it automatically creates perfect clips from videos using AI! 🚀

Use my referral code "${stats.referral_code}" and we both get an extra video per month! 

${stats.referral_url}

#VideoEditing #AI #ContentCreator`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          <p>Failed to load referral information</p>
          <button 
            onClick={loadReferralStats}
            className="mt-2 btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-3">
            <Users className="w-6 h-6 text-gray-700" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.successful_referrals}</h3>
          <p className="text-gray-600">Successful Referrals</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-3">
            <Gift className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.referral_bonus_videos}</h3>
          <p className="text-gray-600">Bonus Videos Earned</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 text-center border border-gray-200">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-3">
            <Share2 className="w-6 h-6 text-gray-700" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">{stats.referral_code || '---'}</h3>
          <p className="text-gray-600">Your Referral Code</p>
        </div>
      </div>

      {/* Referral Code Display */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Code</h3>
        
        <div className="space-y-4">
          {/* Referral Code */}
          <div className="text-center">
            <div className="inline-flex items-center space-x-3 bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
              <span className="text-2xl font-bold text-gray-900 tracking-wider">
                {stats.referral_code || 'Loading...'}
              </span>
              <button
                onClick={() => stats.referral_code && copyToClipboard(stats.referral_code)}
                disabled={!stats.referral_code}
                className={`p-2 rounded-lg transition-colors border ${
                  !stats.referral_code 
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : copied 
                      ? 'bg-red-50 text-red-700 border-red-200' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                title={stats.referral_code ? "Copy referral code" : "Loading referral code..."}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {stats.referral_code ? 'Share this code with friends' : 'Generating your referral code...'}
            </p>
          </div>
        </div>
      </div>

      {/* Referral Link Sharing */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Share Your Referral Link</h3>
        
        {stats.referral_url && (
          <div className="space-y-4">
            {/* Copy Link */}
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-sm text-gray-700 break-all">{stats.referral_url}</p>
              </div>
              <button
                onClick={() => copyToClipboard(stats.referral_url!)}
                className={`p-3 rounded-lg transition-colors border ${
                  copied 
                    ? 'bg-red-50 text-red-700 border-red-200' 
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={shareViaEmail}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg transition-colors text-gray-700"
              >
                <span>📧</span>
                <span>Share via Email</span>
                <ExternalLink className="w-4 h-4" />
              </button>

              <button
                onClick={shareViaTwitter}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <span>🐦</span>
                <span>Share on Twitter</span>
                <ExternalLink className="w-4 h-4" />
              </button>

              <button
                onClick={() => copyToClipboard(stats.referral_code)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-lg transition-colors"
              >
                <Copy className="w-4 h-4" />
                <span>Copy Code Only</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">How Referrals Work</h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start space-x-2">
            <span className="text-red-600 font-bold">1.</span>
            <span>Share your referral link or code with friends</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-red-600 font-bold">2.</span>
            <span>When they sign up using your code, you both get +1 video per month</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-red-600 font-bold">3.</span>
            <span>Referral bonuses stack - invite more friends for more videos!</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-red-600 font-bold">4.</span>
            <span>Bonus videos reset monthly and don't expire</span>
          </li>
        </ul>
      </div>

      {/* Referred by section */}
      {stats.referred_by && (
        <div className="bg-red-50 rounded-lg p-6 border border-red-200">
          <h3 className="text-lg font-semibold text-red-900 mb-2">You Were Referred!</h3>
          <p className="text-red-800">
            You joined using referral code <span className="font-bold">{stats.referred_by}</span>. 
            Thanks for using a friend's referral! You both earned an extra video per month.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;
